/**
 * VisitasPage — rota /visitas (RF-M7-001, US-08 AC3).
 * Formulário de filtros combinados (todos os da US-01) + tabela paginada.
 * Consome GET /visitas?filtros real. A lista NÃO traz viewer_url (RN-M7-003).
 * ADR-036 (design §5): "Reprocessar estas N visitas" (admin) com o filtro APLICADO e o `total` dele; os filtros
 * `lote`, `reprocessamento` (+ `mudou`) e `pendencia` chegam pela URL (Lotes, acompanhamento, painel) e aparecem como
 * etiqueta removível; a linha inteira abre a visita (clique em qualquer ponto, Enter).
 * ADR-038 (design §9): a etiqueta da evidência em cada linha ("Com imagem" / "Planilha (sem imagem)" e, na linha de
 * referência, "Referência — há termo com imagem") e o filtro Evidência (com "Incluir referências"), que também chega
 * pela URL (`evidencia`, `incluirReferencias=1` — atalho do painel) e vai junto no "Exportar este resultado".
 */

import { KeyboardEvent, MouseEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useVisitas } from '../hooks/useConsulta';
import { useStatusReprocessamento } from '../hooks/usePainel';
import { useAuth, hasAnyProfile } from '../contexts/AuthContext';
import { Evidencia, VisitaFiltros, Marcador, PendenciaVisita } from '../services/consulta-types';
import { filtroAplicado } from '../services/consultaApi';
import { MarcadorPill, NomeEscola, Pager } from '../components/ConsultaShared';
import { ReprocessarDialog, rotuloReprocessarFiltro } from '../components/ReprocessarDialog';
import { formatarData, formatarDataVisita } from '../components/format';
import { ROTULO_PENDENCIA } from '../components/reprocessamentoTextos';
import { filtroParaExportacao } from '../services/exportacoes-types';
import { EtiquetaEvidencia, FiltroEvidencia } from '../components/Evidencia';

const MARCADORES: readonly Marcador[] = ['OK', 'CORRIGIDO', 'ALERTA', 'ERRO'];

/** Filtros que só chegam pela URL e aparecem como etiqueta (ADR-036). `mudou` vai junto com `reprocessamento`. */
type FiltroDaUrl = 'lote' | 'reprocessamento' | 'pendencia' | 'outrasEvidencias';
const FILTROS_DA_URL: readonly FiltroDaUrl[] = ['lote', 'reprocessamento', 'pendencia', 'outrasEvidencias'];

/**
 * Os filtros da URL: `pasta` (link "N visitas" de Pastas — ADR-033), `lote` ("Ver as N visitas" de Lotes),
 * `reprocessamento` + `mudou=1` (acompanhamento) e `pendencia` (sugestões do painel). Pendência desconhecida e
 * `mudou` sem reprocessamento são ignorados.
 */
function filtrosDaUrl(params: URLSearchParams): VisitaFiltros {
  const pasta = params.get('pasta');
  const lote = params.get('lote');
  const reprocessamento = params.get('reprocessamento');
  const pendencia = params.get('pendencia');
  const evidencia = params.get('evidencia');
  const comEvidencia = evidencia === 'imagem' || evidencia === 'planilha';
  // ADR-040: o atalho do relatório da carga ("as visitas da planilha com alerta").
  const marcador = params.get('marcador');
  const comMarcador = marcador === 'OK' || marcador === 'CORRIGIDO' || marcador === 'ALERTA' || marcador === 'ERRO';
  return {
    ...(comMarcador ? { marcadores: marcador as Marcador } : {}),
    ...(pasta ? { pasta } : {}),
    ...(comEvidencia ? { evidencia: evidencia as Evidencia } : {}),
    ...(evidencia === 'planilha' && params.get('incluirReferencias') === '1' ? { incluirReferencias: true } : {}),
    ...(lote ? { lote } : {}),
    ...(reprocessamento ? { reprocessamento } : {}),
    ...(reprocessamento && params.get('mudou') === '1' ? { mudou: true } : {}),
    ...(pendencia && pendencia in ROTULO_PENDENCIA ? { pendencia: pendencia as PendenciaVisita } : {}),
    // Spec ajustes-telas-amostra: o link compartilhado abre com "Com outras evidências".
    ...(params.get('outrasEvidencias') === '1' ? { outrasEvidencias: true } : {}),
  };
}

function semFiltroDaUrl(f: VisitaFiltros, chave: FiltroDaUrl): VisitaFiltros {
  const { [chave]: _removido, ...resto } = f;
  if (chave !== 'reprocessamento') return resto;
  const { mudou: _mudou, ...semMudou } = resto;
  return semMudou;
}

/** "a", "a e b", "a, b e c". */
function emLista(itens: readonly string[]): string {
  return itens.length <= 1 ? itens.join('') : `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

export function VisitasPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const inicial = filtrosDaUrl(params);
  const [rascunho, setRascunho] = useState<VisitaFiltros>(inicial);
  const [page, setPage] = useState(1);
  const [aplicados, setAplicados] = useState<VisitaFiltros>(inicial);
  const [reprocessarAberto, setReprocessarAberto] = useState(false);
  const navigate = useNavigate();
  const podeReprocessar = hasAnyProfile(useAuth(), ['admin']);
  // Exporta exatamente o que está na tela: os filtros APLICADOS (não o rascunho). A exportação
  // (M9) não filtra por observações nem pelos filtros do reprocessamento (ADR-036): com um deles
  // aplicado, o arquivo sairia diferente da tela — o botão fica desabilitado, com o motivo.
  const naoExportaveis = [
    aplicados.observacoes === true ? 'observações' : null,
    aplicados.lote ? 'lote' : null,
    aplicados.reprocessamento ? 'reprocessamento' : null,
    aplicados.pendencia ? 'pendência' : null,
  ].filter((n): n is string => n !== null);
  const exportacaoIndisponivel = naoExportaveis.length > 0;
  const exportarResultado = (): void => {
    const filtro = filtroParaExportacao({ ...aplicados });
    navigate('/exportacoes', {
      state: Object.keys(filtro).length > 0 ? { escopo: 'filtro', filtro } : { escopo: 'tudo' },
    });
  };
  // Paginação por cursor (contrato): cursor = String(page); limit = tamanho fixo.
  const { pagina, carregando, erro } = useVisitas({
    ...aplicados,
    cursor: page > 1 ? String(page) : undefined,
    limit: 50,
  });

  const set = (parcial: Partial<VisitaFiltros>): void =>
    setRascunho((f) => ({ ...f, ...parcial }));

  const aplicar = (): void => {
    setPage(1);
    setAplicados({ ...rascunho });
    // "Com outras evidências" vai na URL (o link compartilhado abre filtrado — spec ajustes-telas-amostra, Req 2.3).
    if ((params.get('outrasEvidencias') === '1') !== (rascunho.outrasEvidencias === true)) {
      const proximos = new URLSearchParams(params);
      if (rascunho.outrasEvidencias === true) proximos.set('outrasEvidencias', '1');
      else proximos.delete('outrasEvidencias');
      setParams(proximos, { replace: true });
    }
  };
  const limpar = (): void => {
    setRascunho({});
    setPage(1);
    setAplicados({});
    setParams(new URLSearchParams(), { replace: true });
  };
  // Tira o filtro da etiqueta da tela e da URL (recarregar não o traz de volta).
  const removerFiltro = (chave: FiltroDaUrl): void => {
    setRascunho((f) => semFiltroDaUrl(f, chave));
    setAplicados((f) => semFiltroDaUrl(f, chave));
    setPage(1);
    const proximos = new URLSearchParams(params);
    proximos.delete(chave);
    if (chave === 'reprocessamento') proximos.delete('mudou');
    setParams(proximos, { replace: true });
  };

  // A data do reprocessamento na etiqueta vem do acompanhamento (rota do administrador); sem ele, sem a data.
  const { status: reprocessamentoDaEtiqueta } = useStatusReprocessamento(
    podeReprocessar && aplicados.reprocessamento ? aplicados.reprocessamento : null,
  );
  const textoEtiqueta = (chave: FiltroDaUrl): string | null => {
    if (chave === 'lote') return aplicados.lote ? `Visitas do lote ${aplicados.lote.slice(0, 8)}` : null;
    if (chave === 'pendencia') return aplicados.pendencia ? ROTULO_PENDENCIA[aplicados.pendencia] : null;
    if (chave === 'outrasEvidencias') return aplicados.outrasEvidencias === true ? 'Com outras evidências' : null;
    if (!aplicados.reprocessamento) return null;
    const quando = reprocessamentoDaEtiqueta ? ` de ${formatarData(reprocessamentoDaEtiqueta.criadoEm)}` : '';
    return `Visitas do reprocessamento${quando}${aplicados.mudou ? ' — só as que mudaram' : ''}`;
  };
  const etiquetas = FILTROS_DA_URL.flatMap((chave) => {
    const texto = textoEtiqueta(chave);
    return texto ? [{ chave, texto }] : [];
  });

  // O conjunto do reprocessamento é o filtro APLICADO (o da tela), com o total dele — não a página.
  const filtroDaTela = filtroAplicado(aplicados);
  const total = pagina?.total;
  const podeReprocessarLista = podeReprocessar && total !== undefined && total > 0;

  const abrirVisita = (id: string): void => navigate(`/visitas/${id}`);
  // A linha inteira abre a visita; um link ou botão dentro dela mantém a própria ação (Req 7.3).
  const clicarLinha = (e: MouseEvent<HTMLTableRowElement>, id: string): void => {
    if ((e.target as HTMLElement).closest('a, button, input, select')) return;
    abrirVisita(id);
  };
  const teclaLinha = (e: KeyboardEvent<HTMLTableRowElement>, id: string): void => {
    if (e.key === 'Enter' && e.target === e.currentTarget) abrirVisita(id);
  };

  return (
    <div>
      <h1 className="page-title">Visitas</h1>
      <p className="page-subtitle">
        Localize qualquer visita por escola, UF, município, IMEI, ICCID, CNPJ, marcadores ou pasta.
      </p>

      <section className="card">
        <div className="filtros">
          <Campo id="q" label="Busca livre" valor={rascunho.q} onChange={(v) => set({ q: v })} />
          <Campo id="uf" label="UF" valor={rascunho.uf} onChange={(v) => set({ uf: v })} />
          <Campo id="inep" label="INEP" valor={rascunho.escolaInep} onChange={(v) => set({ escolaInep: v })} />
          <Campo id="mun" label="Município (IBGE)" valor={rascunho.municipioIbge} onChange={(v) => set({ municipioIbge: v })} />
          <div className="field">
            <label htmlFor="f-de">De</label>
            <input id="f-de" type="date" value={rascunho.de ?? ''} onChange={(e) => set({ de: e.target.value || undefined })} />
          </div>
          <div className="field">
            <label htmlFor="f-ate">Até</label>
            <input id="f-ate" type="date" value={rascunho.ate ?? ''} onChange={(e) => set({ ate: e.target.value || undefined })} />
          </div>
          <Campo id="res" label="Resultado" valor={rascunho.resultado} onChange={(v) => set({ resultado: v })} />
          <Campo id="tec" label="Técnico" valor={rascunho.tecnico} onChange={(v) => set({ tecnico: v })} />
          <Campo id="contr" label="Contratada" valor={rascunho.contratada} onChange={(v) => set({ contratada: v })} />
          <Campo id="imei" label="IMEI" valor={rascunho.imei} onChange={(v) => set({ imei: v })} />
          <Campo id="iccid" label="ICCID" valor={rascunho.iccid} onChange={(v) => set({ iccid: v })} />
          <Campo id="cnpj" label="CNPJ" valor={rascunho.cnpj} onChange={(v) => set({ cnpj: v })} />
          <Campo id="pasta" label="Pasta (prefixo)" valor={rascunho.pasta} onChange={(v) => set({ pasta: v })} />
          <div className="field">
            <label htmlFor="f-marc">Marcador</label>
            <select
              id="f-marc"
              value={rascunho.marcadores ?? ''}
              onChange={(e) => set({ marcadores: (e.target.value || undefined) as Marcador | undefined })}
            >
              <option value="">Todos</option>
              {MARCADORES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <FiltroEvidencia
            id="f-evidencia"
            valor={{ evidencia: rascunho.evidencia, incluirReferencias: rascunho.incluirReferencias }}
            onMudar={(v) => set({ evidencia: v.evidencia, incluirReferencias: v.incluirReferencias })}
          />
          <div className="field">
            <label htmlFor="f-obs">
              <input
                id="f-obs"
                type="checkbox"
                checked={rascunho.observacoes === true}
                onChange={(e) => set({ observacoes: e.target.checked || undefined })}
              />{' '}
              Com observações
            </label>
          </div>
          <div className="field">
            <label htmlFor="f-outras-evidencias">
              <input
                id="f-outras-evidencias"
                type="checkbox"
                checked={rascunho.outrasEvidencias === true}
                onChange={(e) => set({ outrasEvidencias: e.target.checked || undefined })}
              />{' '}
              Com outras evidências
            </label>
          </div>
        </div>
        {etiquetas.length > 0 && (
          <div className="etiquetas-filtro">
            {etiquetas.map(({ chave, texto }) => (
              <span key={chave} className="etiqueta-filtro" data-testid={`etiqueta-filtro-${chave}`}>
                {texto}
                <button
                  type="button"
                  className="etiqueta-remover"
                  aria-label={`Remover o filtro: ${texto}`}
                  onClick={() => removerFiltro(chave)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="row-actions">
          <button className="btn btn-primary" onClick={aplicar}>
            Aplicar filtros
          </button>
          <button className="btn btn-ghost" onClick={limpar}>
            Limpar
          </button>
          <button className="btn btn-ghost" onClick={exportarResultado} disabled={exportacaoIndisponivel}>
            Exportar este resultado
          </button>
          {exportacaoIndisponivel && (
            <span className="muted">{`A exportação ainda não filtra por ${emLista(naoExportaveis)}.`}</span>
          )}
        </div>
      </section>

      <section className="card">
        <h2>Resultados</h2>
        {podeReprocessarLista && (
          <div className="row-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setReprocessarAberto(true)}>
              {rotuloReprocessarFiltro(filtroDaTela, total)}
            </button>
          </div>
        )}
        {carregando && <p className="muted">Carregando…</p>}
        {erro && <p className="notice">Não foi possível carregar as visitas.</p>}
        {!carregando && !erro && (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Escola</th>
                  <th>Município / UF</th>
                  <th>Resultado</th>
                  <th>Marcador</th>
                  <th>Evidência</th>
                  <th>Arquivo</th>
                </tr>
              </thead>
              <tbody>
                {(pagina?.itens ?? []).map((v) => (
                  <tr
                    key={v.id}
                    className="linha-clicavel"
                    tabIndex={0}
                    onClick={(e) => clicarLinha(e, v.id)}
                    onKeyDown={(e) => teclaLinha(e, v.id)}
                  >
                    <td>
                      <Link to={`/visitas/${v.id}`}>{v.dataVisita ? formatarDataVisita(v.dataVisita) : '—'}</Link>
                    </td>
                    <td data-testid={`escola-${v.id}`}>
                      <NomeEscola termo={v.nomeEscolaTermo} oficial={v.nomeEscola} reserva={v.escolaInep} />
                    </td>
                    <td>
                      {v.municipio ?? '—'} {v.uf ? `/ ${v.uf}` : ''}
                    </td>
                    <td>{v.resultado ?? '—'}</td>
                    <td>
                      <MarcadorPill marcador={v.marcador} />
                    </td>
                    <td>
                      <EtiquetaEvidencia evidencia={v.evidencia} referencia={v.referencia === true} fonte={v.fonte} />
                    </td>
                    <td className="mono">{v.nomeArquivo ?? '—'}</td>
                  </tr>
                ))}
                {(pagina?.itens.length ?? 0) === 0 && (
                  <tr>
                    <td colSpan={7} className="muted">
                      Nenhuma visita encontrada com os filtros atuais.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pager
              page={page}
              temProxima={pagina?.nextCursor !== null && pagina?.nextCursor !== undefined}
              onMudar={(p) => setPage(p)}
            />
          </>
        )}
      </section>

      {reprocessarAberto && podeReprocessarLista && (
        <ReprocessarDialog
          alvo={{ tipo: 'filtro', filtro: filtroDaTela, total }}
          onFechar={() => setReprocessarAberto(false)}
        />
      )}
    </div>
  );
}

function Campo({
  id,
  label,
  valor,
  onChange,
}: {
  id: string;
  label: string;
  valor: string | undefined;
  onChange: (v: string | undefined) => void;
}): JSX.Element {
  return (
    <div className="field">
      <label htmlFor={`f-${id}`}>{label}</label>
      <input
        id={`f-${id}`}
        type="text"
        value={valor ?? ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      />
    </div>
  );
}
